// Phase 6 — Track B T-B3. Error decoder for the shared ApiClient.
//
// Every non-2xx response throws an `ApiError`. 401 specifically throws a
// subclass `AuthRequiredError` so the web layer's react-query global onError
// can pattern-match without sniffing status codes.
//
// `NetworkError` wraps fetch-thrown errors (DNS failure, offline, CORS) so
// callers can render a single `errors.network_unavailable` copy string
// without care for the underlying cause.

import type { ApiErrorEnvelope } from '../types/index.js';

export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly fields: Record<string, readonly string[]> | undefined;

  constructor(args: {
    code: string;
    message: string;
    status: number;
    fields?: Record<string, readonly string[]>;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.code = args.code;
    this.status = args.status;
    this.fields = args.fields;
    // Preserve prototype across transpilation (Tailwind/Vite SWC can subtly
    // break instanceof checks otherwise).
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export class AuthRequiredError extends ApiError {
  constructor(args: {
    code: string;
    message: string;
    status: 401;
    fields?: Record<string, readonly string[]>;
  }) {
    super(args);
    this.name = 'AuthRequiredError';
    Object.setPrototypeOf(this, AuthRequiredError.prototype);
  }
}

export class NetworkError extends ApiError {
  public override readonly cause: unknown;
  constructor(message: string, cause: unknown) {
    super({ code: 'network_unavailable', message, status: 0 });
    this.name = 'NetworkError';
    this.cause = cause;
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Decode the response body into an ApiError. Tolerates non-JSON bodies (e.g.
 * a 502 from a reverse proxy returning HTML) by falling back to a generic
 * `server_error` code.
 */
export async function decodeError(response: Response): Promise<ApiError> {
  let envelope: ApiErrorEnvelope | null = null;
  try {
    const text = await response.text();
    if (text) {
      const parsed = JSON.parse(text) as unknown;
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        'error' in parsed &&
        typeof (parsed as { error: unknown }).error === 'object' &&
        (parsed as { error: unknown }).error !== null &&
        'code' in (parsed as { error: { code: unknown } }).error &&
        typeof (parsed as { error: { code: unknown } }).error.code === 'string'
      ) {
        envelope = parsed as ApiErrorEnvelope;
      }
    }
  } catch {
    // Non-JSON body. Fall through to fallback.
  }

  const fallbackCode = response.status >= 500 ? 'server_error' : 'unknown';
  const code = envelope?.error.code ?? fallbackCode;
  const message =
    envelope?.error.message ??
    `Request failed with status ${response.status}.`;
  const fields = envelope?.error.fields;

  if (response.status === 401) {
    return new AuthRequiredError({
      code,
      message,
      status: 401,
      ...(fields !== undefined ? { fields } : {}),
    });
  }
  return new ApiError({
    code,
    message,
    status: response.status,
    ...(fields !== undefined ? { fields } : {}),
  });
}
